import type {
  ProductBacklogMoveRecord,
  ProductBacklogMovesResponse,
  ProductBacklogSectionKind,
} from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 結果の札の色。
 * @graph-connects none
 */
const OUTCOME_VARIANT: Record<
  ProductBacklogMoveRecord['outcome'],
  'success' | 'warning' | 'error' | 'default'
> = {
  applied: 'success',
  skipped: 'warning',
  drifted: 'error',
  unverified: 'error',
  undone: 'default',
  drift_found: 'error',
};

/**
 * 日本時間（UTC+9）。
 * @graph-connects none
 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

/**
 * 記録の時刻の書き方（日本時間の「10/09 15:20」）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 動かした記録の時刻を、日本時間の月/日 時:分で書く
 * @graph-connects none
 */
export function formatRecordAt(iso: string): string {
  const jst = new Date(new Date(iso).getTime() + JST_OFFSET_MS).toISOString();
  return `${jst.substring(5, 7)}/${jst.substring(8, 10)} ${jst.substring(11, 16)}`;
}

/**
 * 動かした人の書き方（メールの @ より前。ずれの発見のように人がいなければ —）。
 * @graph-connects none
 */
function personOf(by: string | null): string | null {
  return by === null ? null : by.replace(/@.*$/, '');
}

/**
 * 動かした記録の小窓。新しい順に1回の反映ごとにまとめて並べ、直前の反映にだけ［元に戻す］を付ける
 * （動かす人だけ）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件を動かした記録（いつ・誰・案件・どこからどこへ・結果・理由）を新しい順に小窓で出し、動かす人には直前の反映を元に戻すボタンを出す
 * @graph-connects formatRecordAt [calls] 時刻の書き方
 * @graph-connects personOf [calls] 人の書き方
 */
export function ProductBacklogMovesModal({
  open,
  onClose,
  data,
  loading,
  failed,
  canUndo,
  undoing,
  onUndo,
}: {
  open: boolean;
  onClose: () => void;
  data: ProductBacklogMovesResponse | undefined;
  loading: boolean;
  failed: boolean;
  canUndo: boolean;
  undoing: boolean;
  onUndo: (batchId: string) => void;
}) {
  const { t } = useTranslation();
  const place = (kind: ProductBacklogSectionKind, rank: number | null) =>
    kind === 'unassigned' || rank === null
      ? t(`productBacklog.sections.${kind}.name`)
      : t('productBacklog.band.place', {
          section: t(`productBacklog.sections.${kind}.name`),
          rank: String(rank),
        });
  const records = data?.records ?? [];
  return (
    <Modal open={open} onClose={onClose} title={t('productBacklog.records.title')} size="lg">
      {loading && (
        <p className="text-sm text-[var(--color-text-muted)]">{t('productBacklog.loading')}</p>
      )}
      {failed && (
        <p className="text-sm text-[var(--color-error)]">{t('productBacklog.records.error')}</p>
      )}
      {!loading && !failed && records.length === 0 && (
        <p className="text-sm text-[var(--color-text-muted)]">
          {t('productBacklog.records.empty')}
        </p>
      )}
      {records.length > 0 && (
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {(['at', 'by', 'item', 'move', 'outcome', 'reason'] as const).map((column) => (
                <th
                  key={column}
                  className="whitespace-nowrap border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-left text-xs font-semibold text-[var(--color-text-secondary)]"
                >
                  {t(`productBacklog.records.columns.${column}`)}
                </th>
              ))}
              <th className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]" />
            </tr>
          </thead>
          <tbody>
            {records.map((record) => {
              const rows =
                record.drift !== null
                  ? [
                      {
                        key: record.drift.sheetKey,
                        title: null,
                        move: t('productBacklog.records.drift', {
                          row: String(record.drift.rowNumber),
                          sheetKey: record.drift.sheetKey,
                          linkKey: record.drift.linkKey,
                        }),
                      },
                    ]
                  : record.rows.map((row) => ({
                      key: row.key,
                      title: row.title,
                      move: `${place(row.from.kind, row.from.rank)} → ${place(row.to.kind, row.to.rank)}`,
                    }));
              return (
                <Fragment key={record.batchId}>
                  {rows.map((row, index) => (
                    <tr
                      key={`${record.batchId}-${row.key}`}
                      className={index === 0 ? 'border-t-2 border-[var(--color-border)]' : ''}
                    >
                      <td className="whitespace-nowrap border-b border-[var(--color-border)] px-3 py-2 tabular-nums">
                        {formatRecordAt(record.at)}
                      </td>
                      <td className="border-b border-[var(--color-border)] px-3 py-2">
                        {personOf(record.by) ?? t('productBacklog.none')}
                      </td>
                      <td className="max-w-[280px] border-b border-[var(--color-border)] px-3 py-2 font-semibold">
                        {row.title ?? row.key}
                        {row.title !== null && (
                          <span className="ml-1 text-[11px] font-medium text-[var(--color-text-muted)]">
                            {row.key}
                          </span>
                        )}
                      </td>
                      <td className="border-b border-[var(--color-border)] px-3 py-2">
                        {row.move}
                      </td>
                      <td className="border-b border-[var(--color-border)] px-3 py-2">
                        <Badge variant={OUTCOME_VARIANT[record.outcome]}>
                          {t(`productBacklog.records.outcome.${record.outcome}`)}
                        </Badge>
                      </td>
                      <td className="border-b border-[var(--color-border)] px-3 py-2 text-[var(--color-text-secondary)]">
                        {record.reason === null
                          ? ''
                          : t(`productBacklog.records.reason.${record.reason}`)}
                      </td>
                      <td className="border-b border-[var(--color-border)] px-3 py-2">
                        {index === 0 && canUndo && data?.undoableBatchId === record.batchId && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={undoing}
                            onClick={() => {
                              onUndo(record.batchId);
                            }}
                          >
                            {t('productBacklog.band.undo')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
